/**
 * Settings → Support inbox filters. Mail matching a rule skips the Email
 * inbox and lands in its Filtered tab (kept 30 days).
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api } from '../api/client'
import { FIELD_LABELS, type FilterField, type FilterRule } from '../lib/emailInbox'

function errText(err: unknown): string {
  if (err instanceof ApiError) {
    const d = err.detail as { detail?: unknown } | null
    if (d && typeof d.detail === 'string') return d.detail
    if (d && Array.isArray(d.detail)) {
      return d.detail.map((x) => (x && typeof x === 'object' && 'msg' in x ? String((x as { msg: unknown }).msg) : String(x))).join(' ')
    }
    return `That didn't work (error ${err.status}).`
  }
  return "That didn't work. Check your connection and try again."
}

export function SupportInboxFilters() {
  const qc = useQueryClient()
  const rules = useQuery({
    queryKey: ['support-inbox-filters'],
    queryFn: () => api.get<FilterRule[]>('/api/support-inbox/filters'),
  })
  const [field, setField] = useState<FilterField>('from_domain')
  const [value, setValue] = useState('')
  const [note, setNote] = useState('')

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['support-inbox-filters'] })
    qc.invalidateQueries({ queryKey: ['email-messages'] })
  }
  const add = useMutation({
    mutationFn: () => api.post<FilterRule>('/api/support-inbox/filters', {
      field, value: value.trim(), note: note.trim() === '' ? null : note.trim(),
    }),
    onSuccess: () => { setValue(''); setNote(''); refresh() },
  })
  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/api/support-inbox/filters/${id}`),
    onSuccess: refresh,
  })

  return (
    <div>
      <p className="label mb-2">Support inbox filters</p>
      <p className="label mb-3">
        Mail to service@ that matches a rule goes to the Filtered tab of the Email
        inbox instead of the queue. Nothing is deleted; filtered mail is kept 30 days.
      </p>
      {rules.isLoading && <p className="label">Loading…</p>}
      {rules.isError && <p className="text-red-400">{errText(rules.error)}</p>}
      {rules.data && (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr>
                <th className="label pb-1 pr-4">Match</th>
                <th className="label pb-1 pr-4">Value</th>
                <th className="label pb-1 pr-4">Note</th>
                <th className="label pb-1 pr-4">Added by</th>
                <th className="label pb-1"></th>
              </tr>
            </thead>
            <tbody>
              {rules.data.length === 0 && (
                <tr><td colSpan={5} className="py-1.5 font-mono text-[11px] text-paper-trace">No rules yet.</td></tr>
              )}
              {rules.data.map((r) => (
                <tr key={r.id} className="border-t border-ink-500/60">
                  <td className="py-1.5 pr-4 font-mono text-[11px] text-paper-dim align-top">{FIELD_LABELS[r.field] ?? r.field}</td>
                  <td className="py-1.5 pr-4 font-mono text-[11px] text-paper align-top">{r.value}</td>
                  <td className="py-1.5 pr-4 font-mono text-[11px] text-paper-dim align-top">{r.note ?? ''}</td>
                  <td className="py-1.5 pr-4 font-mono text-[11px] text-paper-dim align-top">{r.created_by}</td>
                  <td className="py-1.5 align-top">
                    <button
                      type="button"
                      className="label hover:underline disabled:opacity-50"
                      disabled={remove.isPending}
                      onClick={() => {
                        if (window.confirm(`Delete the rule "${FIELD_LABELS[r.field]} ${r.value}"? Mail it caught stays in Filtered.`)) remove.mutate(r.id)
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {remove.isError && <p className="text-red-400 text-sm mt-2">{errText(remove.error)}</p>}
      <form
        className="mt-3 flex flex-wrap items-end gap-3"
        onSubmit={(e) => { e.preventDefault(); if (value.trim()) add.mutate() }}
      >
        <label>
          <span className="label block mb-1">Match</span>
          <select
            value={field}
            onChange={(e) => setField(e.target.value as FilterField)}
            className="bg-ink-900 border border-ink-500 px-1.5 py-1 font-mono text-[11px] text-paper"
          >
            {(Object.keys(FIELD_LABELS) as FilterField[]).map((f) => (
              <option key={f} value={f}>{FIELD_LABELS[f]}</option>
            ))}
          </select>
        </label>
        <label>
          <span className="label block mb-1">Value</span>
          <input
            type="text" value={value} onChange={(e) => setValue(e.target.value)}
            className="w-56 bg-ink-900 border border-ink-500 px-1.5 py-1 font-mono text-[11px] text-paper"
          />
        </label>
        <label>
          <span className="label block mb-1">Note</span>
          <input
            type="text" value={note} onChange={(e) => setNote(e.target.value)}
            className="w-56 bg-ink-900 border border-ink-500 px-1.5 py-1 font-mono text-[11px] text-paper"
          />
        </label>
        <button
          type="submit"
          className="label-amber hover:underline disabled:opacity-50"
          disabled={add.isPending || !value.trim()}
        >
          {add.isPending ? 'Adding…' : 'Add'}
        </button>
      </form>
      {add.isError && <p className="text-red-400 text-sm mt-2">{errText(add.error)}</p>}
    </div>
  )
}
